// Modal overlay shown until all three credential blocks are configured.
// Each section has its own saving spinner so saving R2 doesn't grey out
// the OpenRouter or GeoNames Save buttons.

import { useState } from "react";
import {
  firstRunSaveCredentials,
  isCommandError,
  type CommandError,
  type CredentialsInput,
} from "../ipc/commands";
import type { FirstRunStatus } from "../types/contract";

type SectionId = "r2" | "openrouter" | "geonames";

interface WizardError {
  category: CommandError["category"] | "unknown";
  message: string;
  detail?: string | null;
}

function toWizardError(e: unknown): WizardError {
  if (isCommandError(e)) {
    return {
      category: e.category,
      message: e.user_message,
      detail: e.detail ?? null,
    };
  }
  return {
    category: "unknown",
    message: String(e),
    detail: null,
  };
}

export function FirstRunWizard({
  status,
  onStatusChange,
}: {
  status: FirstRunStatus;
  onStatusChange: (s: FirstRunStatus) => void;
}) {
  const [openrouterKey, setOpenrouterKey] = useState("");
  const [r2AccessKey, setR2AccessKey] = useState("");
  const [r2Secret, setR2Secret] = useState("");
  const [r2Bucket, setR2Bucket] = useState("");
  const [r2Account, setR2Account] = useState("");
  const [r2Public, setR2Public] = useState("");
  const [geonames, setGeonames] = useState("");
  const [error, setError] = useState<WizardError | null>(null);
  const [savingId, setSavingId] = useState<SectionId | null>(null);

  const save = async (id: SectionId, creds: CredentialsInput) => {
    setError(null);
    setSavingId(id);
    try {
      const next = await firstRunSaveCredentials(creds);
      onStatusChange(next);
    } catch (e) {
      setError(toWizardError(e));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div
      data-testid="first-run"
      className="fixed inset-0 bg-black/85 z-50 overflow-auto p-6 text-fg"
    >
      <h1 className="text-lg font-semibold mb-2">First-run setup</h1>
      <p className="text-fg-muted text-xs mb-4">
        Atlas needs three credentials to run reverse-image search. You can
        save them one at a time; partial setup persists between launches.
      </p>

      <Section
        id="r2"
        title="Cloudflare R2"
        configured={status.r2_configured}
        savingId={savingId}
        canSave={
          isNonEmpty(r2AccessKey) &&
          isNonEmpty(r2Secret) &&
          isNonEmpty(r2Bucket) &&
          isNonEmpty(r2Account) &&
          isValidPublicUrl(r2Public)
        }
        onSave={() =>
          save("r2", {
            r2_access_key_id: trim(r2AccessKey),
            r2_secret_access_key: trim(r2Secret),
            r2_bucket: trim(r2Bucket),
            r2_account_id: trim(r2Account),
            r2_public_url_template: trim(r2Public),
          })
        }
      >
        <Field
          label="Access Key ID"
          value={r2AccessKey}
          onChange={setR2AccessKey}
        />
        <Field
          label="Secret Access Key"
          value={r2Secret}
          onChange={setR2Secret}
          type="password"
        />
        <Field label="Bucket" value={r2Bucket} onChange={setR2Bucket} />
        <Field label="Account ID" value={r2Account} onChange={setR2Account} />
        <Field
          label="Public URL template (must contain {key})"
          value={r2Public}
          onChange={setR2Public}
          placeholder="https://cdn.example.com/{key}"
          hint={
            r2Public.length > 0 && !isValidPublicUrl(r2Public)
              ? "URL must start with https:// and contain the literal {key} placeholder"
              : undefined
          }
        />
      </Section>

      <Section
        id="openrouter"
        title="OpenRouter"
        configured={status.openrouter_configured}
        savingId={savingId}
        canSave={isNonEmpty(openrouterKey)}
        onSave={() =>
          save("openrouter", { openrouter_api_key: trim(openrouterKey) })
        }
      >
        <Field
          label="API Key"
          value={openrouterKey}
          onChange={setOpenrouterKey}
          type="password"
        />
      </Section>

      <Section
        id="geonames"
        title="GeoNames"
        configured={status.geonames_configured}
        savingId={savingId}
        canSave={isNonEmpty(geonames)}
        onSave={() => save("geonames", { geonames_username: trim(geonames) })}
      >
        <Field label="Username" value={geonames} onChange={setGeonames} />
      </Section>

      {error && <ErrorBanner error={error} />}
    </div>
  );
}

function ErrorBanner({ error }: { error: WizardError }) {
  const [showDetail, setShowDetail] = useState(false);
  const categoryLabel: Record<WizardError["category"], string> = {
    invalid_input: "Input",
    auth: "Auth",
    network: "Network",
    internal: "Internal",
    unknown: "Error",
  };
  return (
    <div
      role="alert"
      data-testid="wizard-error"
      data-category={error.category}
      className="mt-3 text-xs space-y-1"
    >
      <div className="text-[color:var(--err)]">
        <span className="font-semibold">{categoryLabel[error.category]}:</span>{" "}
        {error.message}
      </div>
      {error.detail && (
        <div>
          <button
            type="button"
            onClick={() => setShowDetail((s) => !s)}
            className="text-fg-muted underline-offset-2 hover:underline"
          >
            {showDetail ? "Hide details" : "Show details"}
          </button>
          {showDetail && (
            <pre
              data-testid="wizard-error-detail"
              className="mt-1 whitespace-pre-wrap text-[10px] text-fg-muted bg-white/5 rounded p-2"
            >
              {error.detail}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

function trim(s: string): string {
  return s.trim();
}

function isNonEmpty(s: string): boolean {
  return s.trim().length > 0;
}

function isValidPublicUrl(s: string): boolean {
  const t = s.trim();
  return t.startsWith("https://") && t.includes("{key}");
}

function Section({
  id,
  title,
  configured,
  savingId,
  canSave,
  onSave,
  children,
}: {
  id: SectionId;
  title: string;
  configured: boolean;
  savingId: SectionId | null;
  canSave: boolean;
  onSave: () => void;
  children: React.ReactNode;
}) {
  const isSavingThis = savingId === id;
  const disabled = savingId !== null || !canSave;
  return (
    <section
      data-testid={`wizard-section-${id}`}
      className="mb-4 rounded border border-white/10 p-3"
    >
      <div className="flex items-center gap-2 mb-2">
        <h2 className="font-semibold text-sm">{title}</h2>
        <span
          className={
            configured
              ? "text-[10px] text-[color:var(--ok)]"
              : "text-[10px] text-fg-muted"
          }
        >
          {configured ? "✓ configured" : "not yet configured"}
        </span>
      </div>
      <div className="flex flex-col gap-2">{children}</div>
      <button
        type="button"
        onClick={onSave}
        disabled={disabled}
        className="mt-2 text-xs px-3 py-1 rounded bg-white/10 hover:bg-white/20 disabled:opacity-50"
      >
        {isSavingThis ? "Saving…" : "Save"}
      </button>
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: "text" | "password";
  placeholder?: string;
  hint?: string;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-fg-muted">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="rounded bg-white/5 border border-white/10 px-2 py-1 text-fg"
      />
      {hint && <span className="text-[color:var(--warn)] text-[10px]">{hint}</span>}
    </label>
  );
}
